import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, AlertCircle, UserPlus2, ArrowRight, CheckCircle, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/invite/${token}`);
        if (!cancelled) setPreview(res.data);
      } catch (e) {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Einladungs-Link nicht gefunden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      // Redirect to login, then come back
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }
    setAccepting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/invite/${token}/accept`,
        {},
        { withCredentials: true },
      );
      setAccepted(true);
      // Slight delay so the success state is visible
      setTimeout(() => navigate(`/project/${res.data.projectId}`), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || "Einladung konnte nicht angenommen werden.");
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FFB800] animate-spin" />
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <Lock className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
          <h2 className="font-['Outfit'] text-xl text-white mb-2">
            Einladung nicht verfügbar
          </h2>
          <p className="text-[#A0A0B0] text-sm">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-5 text-sm text-[#00F0FF] hover:text-white transition-colors font-['Outfit']"
          >
            Zur Startseite <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="invite-accept-page"
      className="min-h-screen bg-[#05050A] flex items-center justify-center px-4 py-12"
    >
      <div className="max-w-lg w-full glass-surface rounded-2xl p-8 sm:p-10 border border-[#FFB800]/30">
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 flex items-center justify-center mb-5">
            {accepted ? (
              <CheckCircle className="w-7 h-7 text-[#00E5A0]" />
            ) : (
              <UserPlus2 className="w-7 h-7 text-[#FFB800]" />
            )}
          </div>

          {accepted ? (
            <>
              <h1 className="font-['Outfit'] text-2xl text-white font-light mb-2">
                Einladung angenommen
              </h1>
              <p className="text-sm text-[#A0A0B0] mb-1">
                Du hast jetzt Zugriff auf <strong className="text-white">{preview.projectTitle}</strong>.
              </p>
              <p className="text-xs text-[#606070] font-['JetBrains_Mono']">
                Weiterleitung zum Projekt…
              </p>
            </>
          ) : (
            <>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#FFB800] font-['JetBrains_Mono'] mb-2">
                Bilaterale Einladung
              </span>
              <h1 className="font-['Outfit'] text-2xl sm:text-3xl text-white font-light tracking-tight mb-3">
                {preview.projectTitle}
              </h1>
              <p className="text-sm text-[#A0A0B0] mb-1">
                <strong className="text-white">{preview.ownerName || "Der Auftraggeber"}</strong>{" "}
                lädt dich ein, an diesem Projekt mitzuwirken.
              </p>
              <p className="text-xs text-[#909098] mb-5 font-['JetBrains_Mono']">
                {preview.projectCategory}
              </p>
              {preview.projectDescription && (
                <p className="text-sm text-[#C8C8D2] leading-relaxed mb-6 max-w-md whitespace-pre-line">
                  {preview.projectDescription.length > 280
                    ? preview.projectDescription.slice(0, 280) + "…"
                    : preview.projectDescription}
                </p>
              )}

              <ul className="text-left text-xs text-[#A0A0B0] space-y-1.5 mb-6 self-stretch px-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00E5A0] mt-0.5 flex-shrink-0" />
                  Vollen Read-Access auf 3D-Architektur, Roadmap, Stunden- &amp; Kostenanalyse
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00E5A0] mt-0.5 flex-shrink-0" />
                  Änderungsvorschläge erstellen und mit dem Auftraggeber chatten
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00E5A0] mt-0.5 flex-shrink-0" />
                  Eigenen Stundensatz vorschlagen
                </li>
              </ul>

              {!user && (
                <div className="w-full mb-4 p-3 rounded-md bg-[#00F0FF]/5 border border-[#00F0FF]/30 text-[12px] text-[#A0A0B0]">
                  Du brauchst ein Konto, um die Einladung anzunehmen. Klick unten — wir schicken dich
                  kurz zur Anmeldung und danach automatisch zurück.
                </div>
              )}

              {error && (
                <div className="w-full mb-3 flex items-start gap-2 p-3 rounded-md bg-[#FF3B30]/10 border border-[#FF3B30]/30">
                  <AlertCircle className="w-3.5 h-3.5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                  <span className="text-[12px] text-[#FECACA]">{error}</span>
                </div>
              )}

              <button
                data-testid="accept-invite-btn"
                onClick={handleAccept}
                disabled={accepting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#FFB800] text-black font-['Outfit'] font-semibold text-sm hover:bg-white transition-colors disabled:opacity-50"
              >
                {accepting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus2 className="w-4 h-4" />
                )}
                {user ? "Einladung annehmen" : "Anmelden & annehmen"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
